import { ConfigService } from '@anduin/config';
import { Range } from '@anduin/shared';

interface BaseEmailDeliveryHubFilters {
  dateRange?: Range<string>;
  /**
   * @deprecated - use dateRange instead
   */
  min?: string;
  /**
   * @deprecated - use dateRange instead
   */
  max?: string;

  search: string;
}

export interface EmailDeliveryHubSentFilters extends BaseEmailDeliveryHubFilters {
  types: string[];
  showBouncedOnly: boolean;
}

export interface EmailDeliveryHubPendingFilters extends BaseEmailDeliveryHubFilters {
  showDismissedAndResolved: boolean;
}

export interface EmailDeliveryHubFailedFilters extends BaseEmailDeliveryHubFilters {
  showDismissedAndResolved: boolean;
}

export type EmailDeliveryHubFilters = EmailDeliveryHubSentFilters | EmailDeliveryHubPendingFilters | EmailDeliveryHubFailedFilters;
export type EmailDeliveryHubUnsentFilters = EmailDeliveryHubPendingFilters | EmailDeliveryHubFailedFilters;

export const EMAIL_TYPE_GROUP_LABELS = {
  PAYMENTS: 'Payments',
  BILLING: 'Billing',
  COLLECTIONS: 'Collections',
  ENGAGEMENTS: 'Engagements',
  MISCELLANEOUS: 'Miscellaneous',
} as const;

export type EmailTypeGroupLabel = (typeof EMAIL_TYPE_GROUP_LABELS)[keyof typeof EMAIL_TYPE_GROUP_LABELS];

export type EmailTypeFeatureGate = 'billing' | 'engagements' | 'collections' | 'autopay' | 'paymentPlanV4';

export interface EmailTypeDefinition {
  groups: EmailTypeGroupLabel[];
  requiredGates: EmailTypeFeatureGate[];
}

export const EMAIL_TYPE_DEFINITIONS: Record<string, EmailTypeDefinition> = {
  // --- Payments ---
  'Payment Confirmation': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: [] },
  'Payment Failure': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: [] },
  'Client Refund Initiated': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: [] },
  'Payment Plan Created Notification': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['paymentPlanV4'] },
  'Payment Plan Updated Notification': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['paymentPlanV4'] },
  'Notification When Disabling Recurring Payment': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['paymentPlanV4'] },
  'Client Autopay Created Notification': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['autopay'] },
  'Client Autopay Updated Notification': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['autopay'] },
  'Client Autopay Canceled Notification': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['autopay'] },
  'Client Scheduled Autopay Reminder Notification': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['autopay'] },
  'Client Payment Confirmation Auto Draft': { groups: [EMAIL_TYPE_GROUP_LABELS.PAYMENTS], requiredGates: ['engagements', 'billing'] },

  // --- Billing ---
  'Invoice': { groups: [EMAIL_TYPE_GROUP_LABELS.BILLING], requiredGates: [] },
  'Invoice Reversed': { groups: [EMAIL_TYPE_GROUP_LABELS.BILLING], requiredGates: [] },
  'Scheduled Draft Invoice Notification': { groups: [EMAIL_TYPE_GROUP_LABELS.BILLING], requiredGates: ['billing'] },

  // --- Collections ---
  'Smart Statement': { groups: [EMAIL_TYPE_GROUP_LABELS.COLLECTIONS], requiredGates: ['collections'] },

  // --- Engagements ---
  'Engagement Sent': { groups: [EMAIL_TYPE_GROUP_LABELS.ENGAGEMENTS], requiredGates: ['engagements'] },
  'Engagement Executed': { groups: [EMAIL_TYPE_GROUP_LABELS.ENGAGEMENTS], requiredGates: ['engagements'] },
  'Engagement Reassignment': { groups: [EMAIL_TYPE_GROUP_LABELS.ENGAGEMENTS], requiredGates: ['engagements'] },
  'Engagement Prepayment Failure': { groups: [EMAIL_TYPE_GROUP_LABELS.ENGAGEMENTS], requiredGates: ['engagements'] },

  // --- Miscellaneous ---
  'Unknown': { groups: [EMAIL_TYPE_GROUP_LABELS.MISCELLANEOUS], requiredGates: [] },
};

export const EMAIL_TYPE_GROUPS: Record<string, string[]> = Object.values(EMAIL_TYPE_GROUP_LABELS).reduce(
  (acc, group) => {
    acc[group] = Object.entries(EMAIL_TYPE_DEFINITIONS)
      .filter(([, def]) => def.groups.includes(group))
      .map(([type]) => type)
      .sort((a, b) => a.localeCompare(b));
    return acc;
  },
  {} as Record<string, string[]>,
);

export const INCLUDED_EMAIL_TYPES = Object.keys(EMAIL_TYPE_DEFINITIONS);

export function isFeatureGateEnabled(gate: EmailTypeFeatureGate, configService: ConfigService): boolean {
  switch (gate) {
    case 'billing':
      return configService.billingEnabled;
    case 'engagements':
      return configService.engagementsEnabled;
    case 'collections':
      return configService.collectionsEnabled;
    case 'autopay':
      return configService.autopayEnabled;
    case 'paymentPlanV4':
      return configService.paymentPlanV4Enabled;
  }
}

export function isEmailTypeEnabled(emailType: string, configService: ConfigService): boolean {
  const definition = EMAIL_TYPE_DEFINITIONS[emailType];
  if (!definition) {
    return true;
  }
  return definition.requiredGates.every((gate) => isFeatureGateEnabled(gate, configService));
}

export function getEnabledEmailTypes(configService: ConfigService): string[] {
  return Object.keys(EMAIL_TYPE_DEFINITIONS).filter((type) => isEmailTypeEnabled(type, configService));
}

export function getEnabledGroupLabels(configService: ConfigService): EmailTypeGroupLabel[] {
  return Object.values(EMAIL_TYPE_GROUP_LABELS).filter((group) =>
    Object.entries(EMAIL_TYPE_DEFINITIONS).some(
      ([type, def]) => def.groups.includes(group) && isEmailTypeEnabled(type, configService),
    ),
  );
}
